import { useForm } from '@/Hooks/useFormWrapper';
import { OrderLabelPrintRow } from '@/Types';
import {
  orderLabelPrintActions,
  orderLabelPrintAsyncActions,
  orderLabelPrintModalSelectors,
} from '@/ModalWindows/OrderLabelPrintModal/services';
import { AppDispatch } from '@/Redux/ConfigureStore';
import { useDispatch, useSelector } from 'react-redux';
import { salesPageAsyncActions } from '@/Pages/Sales/services';
import { Toasts } from '@/Components/Toast/Toast';
import { localizationUtils } from '@/Utils/localizationUtils';
import { printService } from '@/Electron';
import { useLocalization } from '@/Hooks';
import { globalSelectors } from '@/Redux/Global/selectors';
import { unwrapResult } from '@reduxjs/toolkit';

export type FormData = {
  orderLabelPrintRows: OrderLabelPrintRow[];
  filterLogistics: number[];
  filterStatus: string;
  date: string;
  timeRange: string;
};

export function useOrderLabelPrintForm(closeModalWindow: () => void) {
  const dispatch: AppDispatch = useDispatch();

  const {
    modalWindows: {
      orderLabelPrint: { LabelsPrintedSuccessToast, LabelsCreateSuccessToast },
    },
  } = useLocalization();

  const methods = useForm<FormData>();

  const localization = useSelector(globalSelectors.localization);
  const selectedOrderLabelRows = useSelector(orderLabelPrintModalSelectors.selectedOrderLabelRows);

  const onSubmit = methods.handleSubmit((data: FormData) => {
    dispatch(orderLabelPrintActions.setFilterLogistics(data.filterLogistics));
    dispatch(orderLabelPrintActions.setFilterStatus(data.filterStatus));
  });

  const printLabels = (
    document: string,
    orderLabelIds: number[],
    isPrintPreview: boolean,
    assignPrinter: string,
  ): boolean => {
    let isSuccess = false;

    if (document === '') return isSuccess;

    const orderIds = selectedOrderLabelRows
      .filter((x) => orderLabelIds.includes(x.id))
      .map((x) => x.orderId)
      .filter((value: number, index: number, self: number[]) => self.indexOf(value) === index);

    printService
      .printLabel(document, isPrintPreview, assignPrinter)
      .then(() => {
        dispatch(orderLabelPrintAsyncActions.setPrintedStatusRequest(orderLabelIds));
        dispatch(salesPageAsyncActions.getOrdersByIds(orderIds));
        isSuccess = true;
      })
      .catch((err) => {
        console.error('print error: ', err);
      });

    return isSuccess;
  };

  const showSuccessToastMessage = (trackingNumbers: string[], isPrint: boolean) => {
    let toastText = '';
    const numbersCount = trackingNumbers.length;
    const toastType = isPrint ? LabelsPrintedSuccessToast : LabelsCreateSuccessToast;

    if (numbersCount > 1) {
      toastText = localizationUtils.insertTranslatedParamsToString(
        toastType.messageMany,
        [numbersCount],
        toastType.parts,
        localization,
      );
    }

    if (numbersCount === 1) {
      toastText = localizationUtils.insertParams(toastType.messageSingle, [trackingNumbers[0]]);
    }

    Toasts.showSuccess({ title: toastType.title, text: toastText });
  };

  const onPrintLabels = (isPrint: boolean) => {
    const { date, timeRange } = methods.getValues();

    const orderIds = Array.from(new Set(selectedOrderLabelRows.map((x) => x.orderId)));
    const collectionDate = date && timeRange ? `${date.split('.').reverse().join('/')} ${timeRange}` : undefined;

    dispatch(orderLabelPrintAsyncActions.generateLabelsRequest({ orderIds, collectionDate })).then((response) => {
      dispatch(salesPageAsyncActions.getOrdersByIds(orderIds));

      const trackingNumbers: string[] = [];

      const { labels } = unwrapResult(response);

      labels.forEach(({ labels, pdfLabels, isPrintPreview, assignPrinter }) => {
        labels.forEach((x) => trackingNumbers.push(...x.trackingNumbers));

        showSuccessToastMessage(trackingNumbers, isPrint);

        const orderLabelIds = labels.filter((x) => x.isCreated).map((x) => x.orderLabelId);
        const isPrintSuccess = printLabels(pdfLabels, orderLabelIds, isPrintPreview, assignPrinter);

        if (isPrint && isPrintSuccess) {
          closeModalWindow();
        }
      });
    });
  };

  return {
    methods,
    onSubmit,
    onPrintLabels,
  };
}
